import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { AuthUser, LoginInput } from '@qb/shared';
import { authApi } from '@/api/auth';
import { clearToken, setToken, setOnUnauthorized } from '@/api/http';
import { router } from '@/router';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  /** Set true after we've finished the restoreSession() call on app boot. */
  const ready = ref(false);

  const isAuthenticated = computed(() => user.value !== null);
  const isAdmin = computed(() => user.value?.role === 'ADMIN');

  /**
   * Try to restore an existing session from localStorage. Called once on app
   * mount. If the token is invalid (expired, wrong sig), the http layer will
   * trigger our onUnauthorized handler and clear it.
   */
  async function restoreSession(): Promise<void> {
    ready.value = false;
    try {
      user.value = await authApi.me();
    } catch {
      user.value = null;
    } finally {
      ready.value = true;
    }
  }

  async function login(input: LoginInput): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const response = await authApi.login(input);
      setToken(response.token);
      user.value = response.user;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Login failed';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout();
    } catch {
      // Server-side logout is best-effort for stateless JWT — keep going.
    }
    clearToken();
    user.value = null;
    await router.push({ name: 'login' });
  }

  // When any API call returns 401, drop the user and bounce to /login.
  setOnUnauthorized(() => {
    user.value = null;
    if (router.currentRoute.value.name !== 'login') {
      void router.push({
        name: 'login',
        query: { redirect: router.currentRoute.value.fullPath },
      });
    }
  });

  return {
    user,
    loading,
    error,
    ready,
    isAuthenticated,
    isAdmin,
    restoreSession,
    login,
    logout,
  };
});
